/* eslint-disable no-unused-vars */

import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  TextareaAutosize,
  Typography,
  Chip
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Page, SelectImageButton } from 'src/components';
import { DatePickerRangeComponentV2 } from 'src/components';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { getVoucherDataThunk } from 'src/redux/slices/voucher/thunks';
import { FormValues } from 'src/redux/slices/voucher/types';
import { useFormik } from 'formik';
import { usePermissions, useSnackBar } from 'src/hooks';
import useResolution from 'src/hooks/useResolution';
import { pickBy, isEqual, isEmpty } from 'lodash';
import moment from 'moment';
import { useVouchers } from 'src/hooks/useVouchers';
import HelpIcon from '@material-ui/icons/Help';
import { constants } from 'src/redux/slices/voucher';
import { VoucherScopeSection } from './components/VoucherScopeSection';
import { EcommUserListDialog } from './components/EcommUserListDialog';
import {
  convertImageFileToBase64,
  decimalToPercent,
  percentToDecimal
} from 'src/utils';

const { actions: voucherActions, selectors: voucherSelectors } = slices.voucher;
const { actions: optionActions } = slices.options;

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100%',
    padding: theme.spacing(1)
  },
  container: {
    display: 'flex',
    gap: '10px'
  },
  button: {
    width: 70,
    height: 70,
    overflow: 'hidden'
  },
  image: {
    width: 70,
    height: 70
  }
}));

const VoucherDetails = () => {
  const { id } = useParams();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const { lg, sm, xl } = useResolution();
  const navigate = useNavigate();
  const { canUpdateVoucher } = usePermissions();

  const { generateVoucherCode } = useVouchers();
  const { purchaseIcon, shippingIcon } = constants;

  const isLoading = useAppSelector(voucherSelectors.selectIsLoading);
  const originalDetails = useAppSelector(voucherSelectors.selectVoucherDetails);

  // const [isLoading, dispatch(voucherActions.setIsLoading()] = useState(false);
  const [openEcommList, setOpenEcommList] = React.useState(false);
  const [selectedAppNames, setSelectedAppNames] = useState<string[]>([]);
  const [isAppNamesLoading, setIsAppNamesLoading] = useState<boolean>(false);
  const [appNameOptions, setAppNameOptions] = useState<string[]>([]);

  const discountTypeOptions = [
    { label: 'Fix Amount', value: 'amount' },
    { label: 'By Percentage', value: 'percentage' }
  ];

  const [affiliateName, setAffiliateName] = useState<string>('');

  // band aid for ts error
  const mockAppNameOptions: string[] = [];

  const hasVoucherStarted = useMemo(() => {
    const startDate = new Date(originalDetails?.start_date ?? 0); // fall back 0 meaning Jan 1, 1970
    const now = new Date();
    return now >= startDate;
  }, [originalDetails.start_date]);

  const hasVoucherEnded = useMemo(() => {
    const endDate = new Date(originalDetails?.end_date ?? 0); // fall back 0 meaning Jan 1, 1970
    const now = new Date();
    return now <= endDate;
  }, [originalDetails.end_date]);

  const handleVoucherUpdate = async (newValues: any) => {
    if (!id) {
      console.error('Voucher ID is missing');
      snackbar.show({
        severity: 'error',
        message: 'Voucher ID is missing'
      });
      return;
    }

    const changedValues = pickBy(newValues, (value, key) => {
      const initialValues = formik?.initialValues ?? {}; // Ensure it's always an object
      return !isEqual(value, initialValues[key]);
    });

    const initialIds = formik?.initialValues?.scopes?.map(
      (scope: any) => scope.entity_id
    );
    const updatedIds = changedValues?.voucher_scopes?.flatMap(
      (scope: any) => scope.scope_ids
    );

    // check for scope id difference
    const hasDifference = !updatedIds?.every((id: number) =>
      initialIds?.includes(id)
    );

    //removes voucher_scopes field if initial and updated ids has no difference
    if (!hasDifference) {
      delete changedValues?.voucher_scopes;
    }

    if (isEmpty(changedValues)) {
      snackbar.show({
        severity: 'error',
        message: JSON.stringify('No changes detected')
      });
      return;
    }

    try {
      const updateVoucherRes = unwrapResult(
        await dispatch(
          voucherActions.updateVoucherThunk({
            id: Number(id),
            ...changedValues
          })
        )
      );

      const { errors, message } = updateVoucherRes.originalData;

      if (!updateVoucherRes?.success) {
        console.error(
          'errData',
          updateVoucherRes?.message || 'Error Updating Data'
        );
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ||
              'Failed to update details. Please try again later or contact support.'
        });

        return;
      }

      if (updateVoucherRes?.success) {
        if (updateVoucherRes?.originalData?.data) {
          snackbar.show({
            severity: 'success',
            message: 'Voucher updated successfully'
          });
        }
        getVoucherDetails();
      }
    } catch (error) {
      console.error('Failed to update voucher:', error);
      snackbar.show({
        severity: 'error',
        message: 'Failed to update voucher'
      });
      dispatch(voucherActions.setIsLoading(false));
    }
  };

  // declared seperate intentionally for type error
  const initialValues: FormValues = {};

  const formik = useFormik({
    initialValues,
    onSubmit: handleVoucherUpdate
  });

  const isPercentage = formik.values.discount_type === 'percentage';

  const onClickGenerate = useCallback(async () => {
    const code = await generateVoucherCode();

    if (code) {
      formik.setFieldValue('code', code);
    }
  }, [formik, generateVoucherCode]);

  const getVoucherDetails = useCallback(async () => {
    if (!id) return;

    const getVoucherRes = unwrapResult(
      await dispatch(getVoucherDataThunk(Number(id)))
    );

    if (!getVoucherRes?.success) {
      snackbar.show({
        severity: 'error',
        message: getVoucherRes?.message || 'Error Getting Data'
      });
      console.error(
        'Error fetching voucher data:',
        getVoucherRes?.message || 'Unknown error'
      );
      return;
    }
  }, [id, dispatch, snackbar]);

  const onImageSelected = async (img: File[]) => {
    if (img && img?.length > 0) {
      const firstFile = img[0];
      const imgInBase64: any = await convertImageFileToBase64(firstFile);
      formik.setFieldValue('banner_url', imgInBase64);
    }
  };

  const handleAppNameOptions = async () => {
    setIsAppNamesLoading(true);
    const res = unwrapResult(await dispatch(optionActions.getAppNamesThunk()));
    if (res.success) {
      setAppNameOptions(res.originalData.data.split(','));
    } else {
      snackbar.show({
        severity: 'error',
        message:
          'Failed to get app name options, please refresh page or contact support'
      });
    }
    setIsAppNamesLoading(false);
  };

  useEffect(() => {
    handleAppNameOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getVoucherDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    formik.setFieldValue('app_name', selectedAppNames.join(','));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppNames]);

  // reset form fields if original details is updated
  useEffect(() => {
    formik.resetForm({ values: originalDetails });
    setAffiliateName(originalDetails.affiliate?.full_name || '');
    setSelectedAppNames(originalDetails?.app_name?.split(',') ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalDetails]);

  return (
    <Page className={classes.root} title="Voucher Details">
      <Box p={1}>
        <form>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            {affiliateName ? (
              <Typography variant="h4" gutterBottom>
                Voucher Details - {affiliateName}
              </Typography>
            ) : (
              <Typography variant="h4" gutterBottom>
                Voucher Details
              </Typography>
            )}

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/app/vouchers')}
            >
              Back
            </Button>
          </Box>
          <Box mb={2}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              style={{
                marginBottom: '1em',
                alignItems: 'end',
                rowGap: '.5em'
              }}
            >
              {/* voucher type */}
              <Grid item container xs={12} md={12}>
                <Grid item container xs={12} xl={12} md={12}>
                  <Grid item xs={12} md={12}>
                    <Typography variant="h6">Voucher Type:</Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12} xl={12} style={{ display: 'flex' }}>
                  <Button
                    className={classes.button}
                    color="primary"
                    onClick={() =>
                      formik.setFieldValue('voucher_icon_url', purchaseIcon)
                    }
                    style={{
                      opacity:
                        formik.values.voucher_icon_url === purchaseIcon
                          ? 1
                          : 0.5,
                      filter:
                        formik.values.voucher_icon_url === purchaseIcon
                          ? 'none'
                          : 'grayscale(100%) brightness(0.7)',
                      pointerEvents: 'auto',
                      marginRight: '.2em'
                    }}
                  >
                    <img
                      src="https://devs-api-assets.s3.ap-east-1.amazonaws.com/assets/voucher/type_icon/purchase_discount.png"
                      alt="Purchase"
                      style={{ width: 79.29, height: 66.69 }}
                    />
                  </Button>

                  <Button
                    className={classes.button}
                    onClick={() =>
                      formik.setFieldValue('voucher_icon_url', shippingIcon)
                    }
                    style={{
                      opacity:
                        formik.values.voucher_icon_url === shippingIcon
                          ? 1
                          : 0.5,
                      filter:
                        formik.values.voucher_icon_url === shippingIcon
                          ? 'none'
                          : 'grayscale(100%) brightness(0.5)',
                      pointerEvents: 'auto'
                    }}
                  >
                    <img
                      src="https://devs-api-assets.s3.ap-east-1.amazonaws.com/assets/voucher/type_icon/shipping_discount.png"
                      alt="Shipping"
                      style={{ width: 79.29, height: 66.69 }}
                    />
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" flexDirection="column">
                  <Typography>Voucher Name:</Typography>
                  <TextField
                    name="name"
                    value={formik?.values?.name}
                    onChange={formik.handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Grid>

              {/* app name */}
              <Grid
                item
                xs={12}
                md={6}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <Typography>Apps where this voucher can be used:</Typography>
                <Autocomplete
                  id="app-name"
                  multiple
                  disabled={
                    isAppNamesLoading || (hasVoucherStarted && hasVoucherEnded)
                  }
                  options={appNameOptions}
                  value={selectedAppNames}
                  renderTags={(value: any, getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip
                        key={`${option}${index}`}
                        variant="outlined"
                        style={{ fontSize: '.8em', height: '2em' }}
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  onChange={(_, newValue: any) => {
                    setSelectedAppNames(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      fullWidth
                      name="app_name"
                      type="multiple"
                    />
                  )}
                />
              </Grid>

              {/* voucher code */}
              <Grid item xs={12} md>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: sm ? 'column' : 'row',
                    justifyContent: 'flex-start',
                    alignItems: sm ? 'flex-start' : 'center'
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ width: '100%' }}
                  >
                    <Typography>Discount Voucher Code:</Typography>
                    <TextField
                      name="code"
                      value={formik.values.code}
                      disabled={hasVoucherStarted && hasVoucherEnded}
                      onChange={(e) => {
                        const newCode = e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, '');
                        if (newCode.length <= 10) {
                          formik.setFieldValue('code', newCode);
                        }
                      }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              columnGap: '1em',
                              marginRight: '-.94em'
                            }}
                          >
                            <Typography variant="body2" color="textSecondary">
                              {(formik.values.code || '').length}/15
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={onClickGenerate}
                              color="primary"
                              style={{ height: '3em' }}
                              disabled={hasVoucherStarted && hasVoucherEnded}
                            >
                              Generate
                            </Button>
                          </div>
                        )
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography>Maximum number of uses:</Typography>
                <TextField
                  // disabled={} // todo: disable when voucher has started in another ticket
                  name="usage_limit"
                  value={formik?.values?.usage_limit}
                  onChange={(e) => {
                    const limit = e.target.value;
                    const numbersOnlyRegex = /^$|^[1-9][0-9]*$/;
                    if (numbersOnlyRegex.test(limit)) {
                      formik.setFieldValue('usage_limit', limit ? +limit : '');
                    }
                  }}
                  inputProps={{
                    min: 1, // Prevent negative values
                    step: 1 // Ensure whole numbers only (no decimals)
                  }}
                  fullWidth
                  size="small"
                  variant="outlined"
                />
              </Grid>

              {/* description */}
              <Grid
                item
                xs={12}
                md={12}
                style={{ ...(xl && !lg && { marginTop: '-1em' }) }}
              >
                <Box display="flex" flexDirection="column">
                  <Typography>Description</Typography>

                  <TextareaAutosize
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    placeholder="Voucher Description"
                    style={{
                      fontFamily: 'roboto',
                      fontSize: '.9em',
                      width: '100%',
                      height: '5em',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      padding: '10px 12px'
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                {formik.values.banner_url && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      alt="banner"
                      style={{
                        width: sm ? 300 : 500,
                        height: undefined,
                        marginBottom: '1rem'
                      }}
                      src={formik.values.banner_url}
                    />
                  </div>
                )}
                <SelectImageButton
                  containerStyle={{ width: '100%' }}
                  title="Select/Update Voucher Banner"
                  helperText="Size Up to 3MB and only jpeg, jpg and png is allowed"
                  multiple={false}
                  onImageSelected={(e) => onImageSelected(e)}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mb={2}>
            <Grid
              container
              alignItems="center"
              style={{
                alignItems: 'end',
                columnGap: '1em',
                rowGap: '1em'
              }}
            >
              <Grid item xs={12} sm={isPercentage ? 12 : 4} lg xl>
                <Typography>Discount Type:</Typography>
                <Autocomplete
                  options={discountTypeOptions}
                  getOptionLabel={(option) => option.label}
                  disabled={hasVoucherStarted}
                  value={
                    discountTypeOptions.find(
                      (opt) => opt.value === formik.values.discount_type
                    ) || null
                  }
                  onChange={(e, val) => {
                    formik.setFieldValue('discount_type', val?.value || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {formik.values.discount_type === 'percentage' ? (
                <>
                  <Grid item xs={12} lg xl>
                    <Typography>Percentage Off:</Typography>
                    <TextField
                      type="number"
                      disabled={hasVoucherStarted && hasVoucherEnded}
                      value={
                        formik.values.amount === null
                          ? ''
                          : formik.values.amount
                      }
                      name=""
                      fullWidth
                      variant="outlined"
                      onChange={(e) => {
                        let value =
                          e.target.value === '' ? null : +e.target.value;
                        if (value !== null && value > 100) value = 100; // Clamp max to 100
                        formik.setFieldValue('amount', value);
                      }}
                      placeholder="Enter Percentage"
                      size="small"
                      inputProps={{
                        min: 0, // Prevent negative values
                        max: 100, // Set a max limit
                        step: 1 // Ensure whole numbers only (no decimals)
                      }}
                      InputProps={{
                        endAdornment: (
                          <Box
                            style={{
                              display: 'flex',
                              justifyContent: 'end',
                              alignItems: 'center',
                              borderLeft: '1px solid rgba(0,0,0,0.2)',
                              width: '1.5em',
                              height: '2.25em'
                            }}
                          >
                            <Typography style={{ fontSize: '1em' }}>
                              %
                            </Typography>
                          </Box>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={10} sm={11} lg xl>
                    <Typography>Percentage Discount Cap:</Typography>
                    <TextField
                      fullWidth
                      disabled={hasVoucherStarted && hasVoucherEnded}
                      value={formik.values.cap_amount}
                      name="cap_amount"
                      onChange={formik.handleChange}
                      type="number"
                      variant="outlined"
                      placeholder="Enter Discount Cap"
                      size="small"
                    />
                  </Grid>
                  <Grid item style={{ height: '2em' }}>
                    <Tooltip title="The maximum discount amount value allowed for this percentage type voucher">
                      <HelpIcon style={{ color: '#373F50' }} />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} lg xl>
                    <Typography>Min. Spend:</Typography>
                    <TextField
                      name="min_spend"
                      disabled={hasVoucherStarted && hasVoucherEnded}
                      placeholder="Enter Amount"
                      InputProps={{
                        startAdornment: <p style={{ marginRight: '1em' }}>₱</p>
                      }}
                      value={formik.values.min_spend}
                      onChange={formik.handleChange}
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm lg container>
                  <Grid item xs={6} sm style={{ paddingRight: '1em' }}>
                    <Typography>Amount Off:</Typography>
                    <TextField
                      name="amount"
                      disabled={hasVoucherStarted && hasVoucherEnded}
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs sm lg={isPercentage ? 4 : 6}>
                    <Typography>Min. Spend:</Typography>
                    <TextField
                      name="min_spend"
                      disabled={hasVoucherStarted && hasVoucherEnded}
                      value={formik.values.min_spend}
                      onChange={formik.handleChange}
                      fullWidth
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              )}
              <Grid
                item
                sm={12}
                lg
                onClick={() => {
                  setOpenEcommList(true);
                }}
              >
                <Typography>Affiliate User:</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Choose Affiliate User"
                  size="small"
                  value={affiliateName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {affiliateName && (
                <Grid item sm={12} lg>
                  <Typography>Affiliate User Incentive:</Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={
                      formik.values.affiliate_incentive === null
                        ? ''
                        : Number(
                            decimalToPercent(
                              formik.values.affiliate_incentive
                            )?.toFixed(3)
                          )
                    }
                    variant="outlined"
                    size="small"
                    onChange={(e) => {
                      let value = e.target.value === '' ? 0 : +e.target.value;

                      const regex = /^\d*\.?\d{0,3}$/;

                      if (regex.test(value.toString())) {
                        if (value > 100) value = 100;

                        const decimal = percentToDecimal(value);

                        formik.setFieldValue(
                          'affiliate_incentive',
                          parseFloat(decimal.toFixed(5))
                        );
                      }
                    }}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 1
                    }}
                    InputProps={{
                      endAdornment: (
                        <Box
                          style={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            borderLeft: '1px solid rgba(0,0,0,0.2)',
                            width: '1.5em',
                            height: '2.25em'
                          }}
                        >
                          <Typography style={{ fontSize: '1em' }}>%</Typography>
                        </Box>
                      )
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          <Box
            mb={2}
            justifyItems={'center'}
            style={{
              ...(!lg && {
                padding: '0 20em'
              })
            }}
          >
            <DatePickerRangeComponentV2
              fromDateValue={formik.values.start_date}
              toDateValue={formik.values.end_date}
              onChangeToDate={(date) => {
                const formattedDate = moment(date, [
                  'MMMM D, YYYY hh:mm:ss A',
                  'YYYY-MM-DD HH:mm:ss'
                ]).format('YYYY-MM-DD HH:mm:ss');
                formik.setFieldValue('end_date', formattedDate);
              }}
              onChangeFromDate={(date) => {
                const formattedDate = moment(date, [
                  'MMMM D, YYYY hh:mm:ss A',
                  'YYYY-MM-DD HH:mm:ss'
                ]).format('YYYY-MM-DD HH:mm:ss');
                formik.setFieldValue('start_date', formattedDate);
                formik.setFieldValue('end_date', null);
              }}
            />
          </Box>

          <Box mt={3} textAlign="center">
            {canUpdateVoucher ? (
              <Button
                disabled={isLoading}
                variant="contained"
                color="primary"
                onClick={() => formik.handleSubmit()}
                fullWidth
              >
                Save Changes
              </Button>
            ) : null}
          </Box>

          <Box mt={3}>
            <Typography variant="h4" style={{ marginBottom: '0.5em' }}>
              Scope Details
            </Typography>
            <VoucherScopeSection
              hasVoucherStarted={hasVoucherStarted}
              voucherId={formik.values.id}
              voucherScopes={formik.values?.scopes || []}
              scopeUser={formik.values?.user?.full_name ?? ''}
              hasVoucherProducts={!!formik.values?.voucher_products?.[0]} // change to boolean
              discountAmount={formik.values.amount}
              discountCap={formik.values.cap_amount}
              discountType={formik.values.discount_type}
              onScopeChange={(scopes) => {
                formik.setFieldValue('voucher_scopes', scopes ? scopes : null);
              }}
            />
          </Box>
        </form>
      </Box>
      <EcommUserListDialog
        isVisible={openEcommList}
        handleClose={() => setOpenEcommList(false)}
        user_id={formik.values.affiliate_user_id}
        onUserSelect={(user) => {
          const { first_name, last_name } = user;
          formik.setFieldValue('affiliate_user_id', user?.id);

          const fullName = `${first_name} ${last_name}`;
          setAffiliateName(fullName);
          setOpenEcommList(false);
        }}
      />
    </Page>
  );
};

export default VoucherDetails;
